<script lang="ts">
  import { agentStatus, chatMessages, sendMessage, clearChat } from '../stores/agent-streaming';
  import type { AgentStatus } from '../types/debugger';
  import { animate } from 'motion';
  import ToolCallDisplay from './ToolCallDisplay.svelte';

  let { onclose }: { onclose?: () => void } = $props();

  let inputText = $state('');
  let messagesEnd: HTMLDivElement | undefined = $state();
  let messagesContainerEl: HTMLDivElement | undefined = $state();

  let isDisabled = $derived(['thinking', 'tool_calling', 'streaming'].includes($agentStatus));

  const statusIndicator: Record<AgentStatus, { class: string; label: string }> = {
    idle: { class: 'bg-text-muted', label: 'Ready' },
    thinking: { class: 'bg-reasoning animate-pulse', label: 'Thinking...' },
    tool_calling: { class: 'bg-accent animate-pulse', label: 'Calling tools...' },
    streaming: { class: 'bg-accent animate-pulse', label: 'Streaming...' },
    error: { class: 'bg-error', label: 'Error' },
  };

  $effect(() => {
    $chatMessages;
    messagesEnd?.scrollIntoView({ behavior: 'smooth' });
    if (messagesContainerEl) {
      const lastMsg = messagesContainerEl.querySelector('[data-chat-msg]:last-child');
      if (lastMsg) {
        animate(lastMsg as HTMLElement, { opacity: [0, 1], y: [12, 0] } as any, { duration: 0.3, ease: 'easeOut' });
      }
    }
  });

  function handleSend() {
    const text = inputText.trim();
    if (!text || isDisabled) return;
    inputText = '';
    sendMessage(text);
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }
</script>

<div class="flex flex-col h-full bg-bg-panel">
  <!-- Header -->
  <div class="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
    <div class="flex items-center gap-2">
      <h2 class="text-sm font-semibold text-text-primary">AI Debugger</h2>
      <div class="flex items-center gap-1.5">
        <div class="w-2 h-2 rounded-full {statusIndicator[$agentStatus].class}"></div>
        <span class="text-[10px] text-text-muted">{statusIndicator[$agentStatus].label}</span>
      </div>
    </div>
    <div class="flex items-center gap-1">
      {#if ($chatMessages).length > 0}
        <button
          onclick={() => clearChat()}
          class="text-[10px] text-text-muted hover:text-text-secondary transition-colors px-2 py-1 rounded hover:bg-bg-hover"
          title="Clear chat"
        >
          Clear
        </button>
      {/if}
      {#if onclose}
        <button
          onclick={onclose}
          class="text-text-muted hover:text-text-secondary transition-colors p-1 rounded hover:bg-bg-hover"
          aria-label="Close chat panel"
        >
          ✕
        </button>
      {/if}
    </div>
  </div>

  <!-- Messages -->
  <div bind:this={messagesContainerEl} class="flex-1 overflow-y-auto px-4 py-3 space-y-3">
    {#if ($chatMessages).length === 0}
      <div class="flex flex-col items-center justify-center h-full text-center">
        <div class="w-12 h-12 rounded-full bg-bg-card flex items-center justify-center mb-3">
          <span class="text-xl">🤖</span>
        </div>
        <p class="text-xs text-text-muted">Ask the AI debugger about campaign executions</p>
      </div>
    {:else}
      {#each $chatMessages as msg (msg.id)}
        {#if msg.role === 'user'}
          <div data-chat-msg class="flex justify-end">
            <div class="max-w-[85%] px-3 py-2 rounded-xl rounded-br-sm bg-user-msg text-sm text-text-primary">
              {msg.content}
            </div>
          </div>
        {:else if msg.role === 'assistant'}
          <div data-chat-msg class="flex justify-start">
            <div class="max-w-[90%]">
              {#if msg.toolCalls?.length}
                {#each msg.toolCalls as tc (tc.id)}
                  <div class="animate-slide-down">
                    <ToolCallDisplay toolCall={tc} />
                  </div>
                {/each}
              {/if}

              {#if msg.content}
                <div class="px-3 py-2 rounded-xl rounded-bl-sm bg-assistant-msg text-sm text-text-primary whitespace-pre-wrap">
                  {msg.content}{#if msg.isStreaming}<span class="inline-block w-0.5 h-4 bg-accent ml-0.5 animate-blink-cursor align-text-bottom"></span>{/if}
                </div>
              {/if}
            </div>
          </div>
        {/if}
      {/each}
      {#if ['thinking', 'tool_calling'].includes($agentStatus)}
        <div class="flex justify-start">
          <div class="flex items-center gap-1 px-3 py-2 rounded-xl bg-assistant-msg">
            <span class="w-1.5 h-1.5 rounded-full bg-text-muted animate-dot-bounce" style="animation-delay: 0s;"></span>
            <span class="w-1.5 h-1.5 rounded-full bg-text-muted animate-dot-bounce" style="animation-delay: 0.16s;"></span>
            <span class="w-1.5 h-1.5 rounded-full bg-text-muted animate-dot-bounce" style="animation-delay: 0.32s;"></span>
          </div>
        </div>
      {/if}
      <div bind:this={messagesEnd}></div>
    {/if}
  </div>

  <!-- Input Area -->
  <div class="border-t border-border p-3 shrink-0">
    <div class="flex items-end gap-2">
      <textarea
        bind:value={inputText}
        onkeydown={handleKeydown}
        disabled={isDisabled}
        placeholder={isDisabled ? 'Waiting for response...' : 'Ask about this execution...'}
        rows={1}
        class="flex-1 bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted outline-none focus:border-accent resize-none max-h-24 disabled:opacity-50 transition-colors"
      ></textarea>
      <button
        onclick={handleSend}
        disabled={!inputText.trim() || isDisabled}
        aria-label="Send message"
        class="p-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-accent hover:bg-accent-hover text-white active:scale-95"
      >
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
        </svg>
      </button>
    </div>
  </div>
</div>
